// --- START OF FILE: src/components/ThankYouPage.js ---
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Button,
  Paper,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { motion } from 'framer-motion';
import { collection, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3B82F6',
    },
    secondary: {
      main: '#10B981', // Green for success/reward
    },
    background: {
      default: '#0F172A',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#F8FAFC',
      secondary: '#CBD5E1',
    },
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  shape: {
    borderRadius: 16,
  },
});

const AnimatedBackground = () => (
  <Box sx={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 0,
    background: 'radial-gradient(ellipse at top left, #1e3a8a 0%, #0f172a 50%, #0f172a 100%)',
  }} />
);

const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract state from the Reward Capture Page (or fallback from direct hit)
  const { reviewUrl, clientId, couponCode, errorFallback, contactInfo } = location.state || {};
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasHandled = useRef(false);

  const generateTokenId = () => {
    const timestamp = Date.now().toString(36);
    const randomNum = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${randomNum}`;
  };

  useEffect(() => {
    const handleToken = async () => {
      if (hasHandled.current) return;
      hasHandled.current = true;

      try {
        // If they arrived without state, they shouldn't be here
        if (!clientId) {
          navigate('/');
          return;
        }

        const existingToken = localStorage.getItem('accessToken');

        if (existingToken) {
          const existingTokenDocRef = doc(db, 'tokens', existingToken);
          const existingTokenSnap = await getDoc(existingTokenDocRef);

          if (!existingTokenSnap.exists()) {
            await generateAndStoreToken();
          }
        } else {
          await generateAndStoreToken();
        }
      } catch (err) {
        console.error('Error handling token:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const generateAndStoreToken = async () => {
      const newTokenId = generateTokenId();
      const tokenDocRef = doc(collection(db, 'tokens'), newTokenId);
      await setDoc(tokenDocRef, {
        tokenId: newTokenId,
        clientId: clientId,
        used: false,
        timestamp: new Date(),
      });
      localStorage.setItem('accessToken', newTokenId);
    };

    handleToken();
  }, [clientId, navigate]);

  const handleGoogleRedirect = () => {
    if (reviewUrl) {
      window.location.href = reviewUrl;
    } else {
      setError('Review URL is missing from configuration.');
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0F172A' }}>
          <CircularProgress size={60} thickness={2} sx={{ color: '#3B82F6' }} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', position: 'relative', overflowX: 'hidden' }}>
        <AnimatedBackground />
        
        <Container 
          sx={{ 
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            padding: { xs: '16px', sm: '24px' }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
            style={{ width: '100%', maxWidth: '600px' }}
          >
            <Paper
              elevation={0}
              sx={{
                padding: { xs: '30px 20px', sm: '40px 30px' },
                borderRadius: '24px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              }}
            >
              {error ? (
                <Alert severity="error" sx={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#FCA5A5' }}>
                  {error}
                </Alert>
              ) : (
                <>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#10B981', mb: 2 }}>
                    {couponCode && !errorFallback ? 'Reward Sent! 🎉' : 'Thank You! 🙌'}
                  </Typography>

                  {couponCode && !errorFallback && (
                    <Box sx={{ 
                      my: 4, 
                      p: 3, 
                      borderRadius: '16px', 
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                      border: '1px dashed rgba(16, 185, 129, 0.5)'
                    }}>
                      <Typography variant="body2" sx={{ color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                        Your Coupon Code
                      </Typography>
                      <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#fff', letterSpacing: '2px' }}>
                        {couponCode}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#94A3B8' }}>
                        A copy of this code has been linked to {contactInfo}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body1" sx={{ color: '#F8FAFC', mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Because you had such a great experience, it would mean the world to our team if you took 10 seconds to share it on Google.
                  </Typography>

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      onClick={handleGoogleRedirect}
                      fullWidth
                      sx={{
                        py: 2,
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        borderRadius: '12px',
                        textTransform: 'none',
                        background: '#ffffff',
                        color: '#1f2937',
                        boxShadow: '0 10px 20px -5px rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        '&:hover': {
                          background: '#f3f4f6',
                          boxShadow: '0 15px 25px -5px rgba(255, 255, 255, 0.3)',
                        }
                      }}
                    >
                      {/* Simple SVG Google Logo */}
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.8 15.71 17.58V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z" fill="#4285F4"/>
                        <path d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.58C14.73 18.24 13.48 18.64 12 18.64C9.13 18.64 6.7 16.7 5.82 14.12H2.14V16.98C3.96 20.59 7.68 23 12 23Z" fill="#34A853"/>
                        <path d="M5.82 14.12C5.6 13.45 5.47 12.74 5.47 12C5.47 11.26 5.6 10.55 5.82 9.88V7.02H2.14C1.39 8.52 0.97 10.22 0.97 12C0.97 13.78 1.39 15.48 2.14 16.98L5.82 14.12Z" fill="#FBBC05"/>
                        <path d="M12 5.36C13.62 5.36 15.07 5.92 16.21 7.01L19.36 3.86C17.45 2.08 14.97 1 12 1C7.68 1 3.96 3.41 2.14 7.02L5.82 9.88C6.7 7.3 9.13 5.36 12 5.36Z" fill="#EA4335"/>
                      </svg>
                      Post on Google
                    </Button>
                  </motion.div>
                </>
              )}
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ThankYouPage;
// --- END OF FILE: src/components/ThankYouPage.js ---